import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { CreateStringValueAccessorProvider, CreateStringValueValidationProvider } from '../value-accessors/string-value-accessor';
import { ImageLookupComponent } from '../../../config/image-lookup/image-lookup.component';
import { SelectItem } from 'primeng/api';
import { HtmlTemplate } from 'src/app/templates/shared/html-template.model';
import { HtmlTemplateService } from 'src/app/templates/shared/html-template.service';
import { CkEditorPoolService } from '../ckeditor-pool.service';
import { PooledCkEditorHost } from '../pooled-ckeditor-host';
import type { Editor } from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';
/**
 * A component allows you to edit html with images.
 * Image selector is in modal.
 * No html templates.
 * Must input "special fields"
 */
@Component({
  selector: 'wl-modular-wysiwyg-editor',
  templateUrl: 'modular-wysiwyg-editor.component.html',
  styleUrls: ['../wysiwyg-editor/ckeditor.less'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    CreateStringValueAccessorProvider(ModularWlWysiwygEditorComponent),
    CreateStringValueValidationProvider(ModularWlWysiwygEditorComponent),
  ],
  standalone: false,
})
export class ModularWlWysiwygEditorComponent extends PooledCkEditorHost implements OnInit, OnChanges {
  @ViewChild('editorHost', { static: false }) private _editorHost: ElementRef<HTMLElement>;
  @ViewChild('imageLookup', { static: false }) private _imageLookup: ImageLookupComponent;
  @ViewChild('addonBar', { static: false }) private _addonBar: ElementRef<HTMLElement>;

  @Input() public required: any;
  @Input() public disabled: any;
  @Input() public cssClass: string = '';
  @Input() public quickInsertListLabel: string = '';
  @Input() public selectedQuickInsert: string = '';
  @Input() public quickInsertList: SelectItem[];
  @Input() public showHtmlTemplates: boolean;
  @Input() public showAddImageButton: boolean = true;

  /**
   * how often does ckEditor update the model
   */
  @Input() public debounce: number = 500;
  @Input() public editorName = 'ckEditor';

  /**
   * Whether to block pasted or dropped images as base64. Default is true.
   */
  @Input() public compact: boolean = false;
  @Input() public blockEmbeddedBase64Images: boolean = true;

  // repeat events from the ckEditor component
  @Output() public change: EventEmitter<string> = new EventEmitter<string>();
  // not sure what object ckEditor is emitting for the ready event
  @Output() public ready: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public blur: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public focus: EventEmitter<Event> = new EventEmitter<Event>();

  public imageLookupImageSrc: string = '';

  public showModal = false;
  public htmlTemplates: Array<HtmlTemplate>;
  public selectedHtmlTemplate: string;
  public _htmlTemplateService: HtmlTemplateService;

  /**
   * constructor
   */

  public constructor(htmlTemplateService: HtmlTemplateService, ckEditorPool: CkEditorPoolService) {
    super(ckEditorPool);
    this._htmlTemplateService = htmlTemplateService;
  }

  protected get editorHostElement() {
    return this._editorHost;
  }

  protected get editorReadOnly() {
    return !!this.disabled;
  }

  ngOnInit() {
    if (this.showHtmlTemplates == true) {
      this._htmlTemplateService.getHtmlTemplates().then((data: Array<HtmlTemplate>) => {
        this.htmlTemplates = data;
        this.htmlTemplates.splice(0, 0, new HtmlTemplate());
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this._updateReadOnly();
    }
  }

  public override handleEditorReadyEvent(editor: Editor): void {
    super.handleEditorReadyEvent(editor);
    this._attachAddonBarToStickyPanel(editor);
  }

  /**
   * Move the add-image / special-fields bar into CKEditor's sticky panel so it
   * stays pinned above the toolbar when the page scrolls, the same as the toolbar.
   * The editor is pooled, so the bar is put back when the editor is released.
   */
  private _attachAddonBarToStickyPanel(editor: Editor): void {
    const addonBar = this._addonBar?.nativeElement;
    const stickyContent: HTMLElement | undefined = (editor.ui.view as any).stickyPanel?.contentPanelElement;
    if (!addonBar || !stickyContent) return;
    const originalParent = addonBar.parentElement;
    const originalNextSibling = addonBar.nextSibling;
    stickyContent.prepend(addonBar);
    // the panel only sticks while the editor has focus; without this, clicking the bar un-sticks it
    editor.ui.focusTracker.add(addonBar);

    this._onEditorRelease(() => {
      editor.ui.focusTracker.remove(addonBar);
      if (originalParent) {
        originalParent.insertBefore(addonBar, originalNextSibling);
      } else {
        addonBar.remove();
      }
    });
  }

  public insertSelectedImage() {
    if (this._imageLookup && this._imageLookup.selectedImage) {
      this._insertHtmlAtSavedSelection('<img src="' + this._imageLookup.selectedImage.AbsolutePath + '">');
    }
  }

  /**
   * show the image selector modal
   */
  public showImageSelector(): void {
    this.showModal = true;
    this._imageLookup.displaySearch();
  }

  /**
   * when the user picked an image
   * @param event
   */
  public handleImageSelected(event: any) {
    this.insertSelectedImage();
    this.showModal = false;
  }

  /**
   * override super class's isValid
   */
  public isValid = (): boolean => {
    return !this.required || (this.value != null && this.value != '');
  };

  /**
   * when the user picks something out of the quick list
   */
  public handleQuickListPicked(): void {
    if (this.selectedQuickInsert) {
      this._insertHtmlAtSavedSelection(this.selectedQuickInsert);
    }
  }

  public handleHtmlTemplateChange(data: string) {
    this._insertHtmlAtSavedSelection(data);
  }
}
